import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type OrderMessageRow =
  Database['public']['Tables']['order_message']['Row']
export type OrderMessageInsert =
  Database['public']['Tables']['order_message']['Insert']

export type OrderMessageWithUser = OrderMessageRow & {
  user?: {
    first_name: string | null
    last_name: string | null
  } | null
}

export const useMessages = (conversation_id: number | undefined | null) => {
  const supabase = useSupabaseClient()

  const { data: messages, isPending } = useQuery({
    queryKey: [queryKeys.orderMessages, conversation_id],
    queryFn: async () => {
      if (!conversation_id) return []
      const { data, error } = await supabase
        .from('order_message')
        .select(
          '*, user:message_author!order_message_user_id_fkey(first_name, last_name)'
        )
        .eq('conversation_id', conversation_id)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data ?? []
    },
    enabled: !!conversation_id,
    refetchInterval: 10_000,
  })

  return {
    messages: messages ?? [],
    isPending,
  }
}

export const useMessageCounts = (conversation_ids: number[]) => {
  const supabase = useSupabaseClient()

  const { data } = useQuery({
    queryKey: [
      queryKeys.orderMessages,
      'counts',
      [...conversation_ids].sort().join(','),
    ],
    queryFn: async () => {
      if (!conversation_ids.length) return new Map<number, number>()
      const { data: rows, error } = await supabase
        .from('order_message')
        .select('conversation_id')
        .in('conversation_id', conversation_ids)

      if (error) throw error

      const map = new Map<number, number>()
      ;(rows ?? []).forEach((r) => {
        if (r.conversation_id == null) return
        map.set(r.conversation_id, (map.get(r.conversation_id) ?? 0) + 1)
      })
      return map
    },
    enabled: conversation_ids.length > 0,
    refetchInterval: 10_000,
  })

  return { counts: data ?? new Map<number, number>() }
}

export type LatestOrderMessagePreview = {
  role: string | null
  content: string | null
}

/** Latest order_message per quote — used to surface chats waiting on the customer. */
export const useLatestOrderMessageByQuoteIds = (quoteIds: number[]) => {
  const supabase = useSupabaseClient()
  const sortedIds = [...quoteIds].sort((a, b) => a - b)

  return useQuery({
    queryKey: [queryKeys.orderMessages, 'latest-by-quote', sortedIds],
    queryFn: async () => {
      const map = new Map<number, LatestOrderMessagePreview>()
      if (sortedIds.length === 0) return map

      const { data, error } = await supabase
        .from('order_message')
        .select('quote_id, created_at, role, content')
        .in('quote_id', sortedIds)
        .order('created_at', { ascending: false })

      if (error) throw error

      for (const row of data ?? []) {
        if (row.quote_id == null || map.has(row.quote_id)) continue
        map.set(row.quote_id, { role: row.role, content: row.content })
      }
      return map
    },
    enabled: sortedIds.length > 0,
    staleTime: 30_000,
  })
}

export const useCreateMessage = (
  conversation_id: number | undefined | null
) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const createMessage = useMutation({
    mutationFn: async (params: {
      role: OrderMessageInsert['role']
      content: OrderMessageInsert['content']
      user_id?: string | null
      attachment_urls?: string[]
    }) => {
      if (!conversation_id) throw new Error('conversation_id is required')
      const { data, error } = await supabase
        .from('order_message')
        .insert({
          conversation_id,
          role: params.role,
          content: params.content,
          user_id: params.user_id ?? null,
          attachment_urls: params.attachment_urls ?? [],
        })
        .select('id')
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orderMessages, conversation_id],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orderMessages, 'counts'],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.conversations],
      })
    },
  })

  return { createMessage }
}
